<template>
    <v-menu v-model="spaceMenu" offset-x :close-on-content-click="false" nudge-top="8">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingSpaces"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeSpaceSid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : 'mdi-format-list-text' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingSpaces">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title class="d-flex align-center">
                                <b>{{ spaceName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="spaceActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToSpaceSettings()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <SpaceInviteDialog>
                                        <template v-slot:activator>
                                            <v-list-item @click="spaceActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </SpaceInviteDialog>
                                    <v-divider class="my-2"></v-divider>
                                    <EntityLeaveDialog entity-type="space">
                                        <template v-slot:activator>
                                            <v-list-item @click="spaceActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-logout</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Leave space</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityLeaveDialog>
                                    <EntityDeleteDialog entity-type="space" :organization-id="activeOrgOid" :space-id="activeSpaceSid" :space-name="spaceName">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Delete space</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityDeleteDialog>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>
                    <b>{{ spaceName }}</b>
                </span>
            </v-tooltip>
        </template>

        <v-list nav dense>
            <v-list-item class="px-0">
                <v-list-item-title class="d-flex align-center">
                    <v-text-field
                        v-model="spaceSearch"
                        placeholder="Filter all spaces..."
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        background-color="grey lighten-4"
                        dense>
                        <template v-slot:append>
                            <SpaceTypeFilterMenu v-model="selectedSpaceTypes" />
                        </template>
                    </v-text-field>
                </v-list-item-title>
            </v-list-item>

            <v-list-item>
                <v-list-item-title class="text-uppercase">spaces</v-list-item-title>
            </v-list-item>

            <template v-if="sortedSpace.length">
                <v-list-item v-for="item in sortedSpace" :key="item.sid" class="pl-0">
                    <v-list-item-title class="d-flex">
                        <v-btn
                            text
                            block
                            @click="openSpace(item.sid, item.iid, item.snid)"
                            class="justify-start text-none"
                            :class="item.space_archival_timestamp || item.space_rested_timestamp ? 'text--disabled' : ''">
                            <v-btn fab x-small elevation="0" class="mr-4" :color="spaceTypeButtonColor(item)">
                                <v-icon small :class="spaceTypeIconColorClass(item)">
                                    {{ spaceTypeIcon(item) }}
                                </v-icon>
                            </v-btn>
                            {{ item.space_long_id }}
                            <v-spacer></v-spacer>
                            <v-tooltip top>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">
                                        {{ spaceRole(item.space_role).icon }}
                                    </v-icon>
                                </template>
                                <span>{{ spaceRole(item.space_role).label }}</span>
                            </v-tooltip>
                        </v-btn>
                    </v-list-item-title>
                </v-list-item>
            </template>

            <v-list-item v-else-if="sortedSpace.length === 0 && !spaceSearch">
                <v-list-item-title>
                    <v-card flat>
                        <v-card-text class="text-center">
                            <p class="text--disabled">Looks like you have no spaces yet.</p>
                        </v-card-text>
                    </v-card>
                </v-list-item-title>
            </v-list-item>

            <v-list-item v-else-if="sortedSpace.length === 0 && spaceSearch">
                <v-list-item-title>
                    <dashboard-empty icon="mdi-information-outline">
                        No matching spaces found.
                        <br />
                        Try a different filter.
                    </dashboard-empty>
                </v-list-item-title>
            </v-list-item>

            <v-divider class="my-2"></v-divider>
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-btn block @click="gotoSpaces()" color="grey lighten-4">View all spaces</v-btn>
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { SPACE_TYPE_ORDER } from '@/utils/spaceTypeMeta'
import { spaceRole } from '@/utils'
import spaceTypeUi from '@/mixins/spaceTypeUi'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'

const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const EntityDeleteDialog = () => import('@/components/EntityDeleteDialog.vue')
const SpaceInviteDialog = () => import('@/modules/space/components/SpaceInviteDialog.vue')
const SpaceTypeFilterMenu = () => import('@/components/SpaceTypeFilterMenu.vue')
const unixEpoch = DateTime.fromMillis(0).toISO()

export default {
    name: 'SpaceMenu',
    mixins: [spaceTypeUi],
    components: {
        DashboardEmpty,
        EntityDeleteDialog,
        SpaceTypeFilterMenu,
        SpaceInviteDialog,
        EntityLeaveDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            spaceMenu: false,
            selectedSpaceTypes: SPACE_TYPE_ORDER.slice(),
            spaceSearch: '',
            spaceActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapState('orgStore', ['educationSpaces', 'researchSpaces', 'datasetSpaces', 'spacesWithPrimarySnapshots', 'fetchingSpaces']),
        ...mapGetters('spaceStore', ['currentSpaceName']),
        spaceName() {
            if (this.$route.params.sid !== undefined && this.currentSpaceName) {
                return this.currentSpaceName
            }
            return 'Select space'
        },
        activeOrgOid() {
            return this.$route.params.oid
        },
        activeSpaceSid() {
            return this.$route.params.sid
        },
        sortedEducationSpaces() {
            let spaces = orderBy(this.educationSpaces, space => space.space_creation_timestamp || unixEpoch, 'desc')
            spaces = spaces.filter(space => !space.space_archival_timestamp && !space.space_rested_timestamp)

            if (this.spaceSearch) {
                return spaces.filter(space => space.space_long_id.toLowerCase().indexOf(this.spaceSearch.toLowerCase()) !== -1)
            }

            return spaces.slice(0, 5)
        },
        sortedResearchSpaces() {
            let spaces = orderBy(this.researchSpaces, space => space.space_creation_timestamp || unixEpoch, 'desc')
            spaces = spaces.filter(space => !space.space_archival_timestamp && !space.space_rested_timestamp)

            if (this.spaceSearch) {
                return spaces.filter(space => space.space_long_id.toLowerCase().indexOf(this.spaceSearch.toLowerCase()) !== -1)
            }

            return spaces.slice(0, 5)
        },
        sortedDatasetSpaces() {
            let spaces = orderBy(this.datasetSpaces, space => space.space_creation_timestamp || unixEpoch, 'desc')
            spaces = spaces.filter(space => !space.space_archival_timestamp && !space.space_rested_timestamp)

            if (this.spaceSearch) {
                return spaces.filter(space => space.space_long_id.toLowerCase().indexOf(this.spaceSearch.toLowerCase()) !== -1)
            }

            return spaces.slice(0, 5)
        },
        sortedSpace() {
            let spaces = []

            if (this.selectedSpaceTypes.includes('courses')) {
                spaces = spaces.concat(this.sortedEducationSpaces)
            }
            if (this.selectedSpaceTypes.includes('projects')) {
                spaces = spaces.concat(this.sortedResearchSpaces)
            }
            if (this.selectedSpaceTypes.includes('datasets')) {
                spaces = spaces.concat(this.sortedDatasetSpaces)
            }

            return orderBy(spaces, space => space.space_creation_timestamp || unixEpoch, 'desc').slice(0, 5)
        }
    },
    methods: {
        spaceRole,
        goToSpaceSettings() {
            this.spaceActionsMenu = false
            if (!this.activeOrgOid || !this.activeSpaceSid) {
                return
            }
            this.$router.push({
                name: 'config-space-general',
                params: { oid: this.activeOrgOid, sid: this.activeSpaceSid }
            })
        },
        openSpace(sid, iid, snid) {
            this.spaceMenu = false

            if ((this.$route.name || '').startsWith('config')) {
                this.$router.push({
                    name: 'config-space-general',
                    params: { ...this.$route.params, sid },
                    query: { ...this.$route.query }
                })
                return
            }

            this.$router.push({ name: 'snapshot-overview', params: { oid: this.$route.params.oid, sid, iid, snid } })
        },

        gotoSpaces() {
            this.spaceMenu = false
            this.$router.push({ name: 'home-spaces', params: { oid: this.$route.params.oid } })
        }
    }
}
</script>
